import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { BASE_TEMPLATE_LIMITS } from '../constants/validationLimits';

/**
 * Shared composable for Quick Reply template validation
 * Works for both Twilio and WhatsApp platforms with configurable settings
 *
 * @param {Ref<Array>} buttons - Ref to buttons array
 * @param {ComputedRef<Object>} variableValidationRules - Variable validation rules from useVariableManagement
 * @param {ComputedRef<Object>} variableValidationState - Variable validation state from useVariableManagement
 * @param {Object} config - Platform configuration
 * @param {string} config.i18nPrefix - i18n key prefix (e.g., 'CONTENT_TEMPLATES' or 'WHATSAPP_TEMPLATES')
 * @param {Object} config.limits - Platform limits object
 * @param {Object} config.fieldMapping - Field name mapping { title: 'title'|'text' }
 * @param {boolean} [config.showIdField=true] - Whether to show/validate ID field (Twilio has it, WhatsApp doesn't)
 */
export function useQuickReplyValidation(
  buttons,
  variableValidationRules,
  variableValidationState,
  config = {}
) {
  const { t } = useI18n();

  const {
    i18nPrefix = 'CONTENT_TEMPLATES',
    limits = {},
    fieldMapping = { title: 'title' },
    showIdField = true,
  } = config;

  // Get field names based on platform
  const titleField = fieldMapping.title || 'title';

  // Get limits with fallbacks
  const getTitleMaxLength = () =>
    limits.QUICK_REPLY_BUTTON_TITLE_MAX_LENGTH ??
    limits.BUTTON_TEXT_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.QUICK_REPLY_BUTTON_TITLE_MAX_LENGTH;

  const getIdMaxLength = () =>
    limits.QUICK_REPLY_BUTTON_ID_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.QUICK_REPLY_BUTTON_ID_MAX_LENGTH;

  // Validator: no variables allowed in quick reply buttons
  const noVariablesInButtons = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.QUICK_REPLY.NO_VARIABLES_IN_BUTTONS`),
    value => {
      if (!value) return true;
      return !/\{\{\d+\}\}/.test(value);
    }
  );

  const rules = computed(() => {
    const buttonRules = {};

    buttons.value.forEach((_, index) => {
      // Title/text field validation
      buttonRules[`button_${index}_${titleField}`] = {
        required,
        maxLength: maxLength(getTitleMaxLength()),
        noVariablesInButtons,
      };

      // ID field validation (only for platforms that support it)
      if (showIdField) {
        buttonRules[`button_${index}_id`] = {
          maxLength: maxLength(getIdMaxLength()),
          noVariablesInButtons,
        };
      }
    });

    return {
      ...buttonRules,
      ...variableValidationRules.value,
    };
  });

  const validationState = computed(() => {
    const state = buttons.value.reduce((acc, button, index) => {
      // Use the correct field name based on platform
      acc[`button_${index}_${titleField}`] = button[titleField];

      // Include ID field only for platforms that support it
      if (showIdField) {
        acc[`button_${index}_id`] = button.id;
      }

      return acc;
    }, {});

    return {
      ...state,
      ...variableValidationState.value,
    };
  });

  const getErrorMessage = (field, v$) => {
    // Variable fields are handled separately
    if (field.startsWith('var_')) {
      return '';
    }

    if (!v$.value[field]?.$error) return '';

    // Handle button field errors
    if (field.startsWith('button_')) {
      const isIdField = field.includes('_id');
      const isTitleField = field.includes(`_${titleField}`);
      const fieldValidator = v$.value[field];

      if (fieldValidator.required?.$invalid) {
        return t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_TITLE_REQUIRED`);
      }

      if (fieldValidator.maxLength?.$invalid) {
        if (isIdField) {
          return t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_ID_TOO_LONG`, {
            max: getIdMaxLength(),
          });
        }
        if (isTitleField) {
          return t(`${i18nPrefix}.CREATE.QUICK_REPLY.BUTTON_TITLE_TOO_LONG`, {
            max: getTitleMaxLength(),
          });
        }
      }

      if (fieldValidator.noVariablesInButtons?.$invalid) {
        return t(`${i18nPrefix}.CREATE.QUICK_REPLY.NO_VARIABLES_IN_BUTTONS`);
      }
    }

    return '';
  };

  return {
    rules,
    validationState,
    getErrorMessage,
  };
}
